import React from 'react';
import { motion } from 'framer-motion';
import { Check, Clock } from 'lucide-react';
import { FormStep } from '../../types/onboarding';

interface ProgressIndicatorProps {
  steps: FormStep[];
  currentStepIndex: number;
  onStepClick?: (stepIndex: number) => void;
  className?: string;
}

export const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({
  steps,
  currentStepIndex,
  onStepClick,
  className = '',
}) => {
  const totalEstimatedTime = steps.reduce((acc, step) => acc + step.estimatedTime, 0);
  const completedTime = steps
    .slice(0, currentStepIndex)
    .reduce((acc, step) => acc + step.estimatedTime, 0);

  return (
    <div className={`bg-white/95 backdrop-blur-sm border-b border-neutral-200/80 sticky top-0 z-30 ${className}`}>
      <div className="max-w-4xl mx-auto px-6 py-6">
        {/* Header with progress summary */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 font-display">
              Intelligent Onboarding
            </h1>
            <p className="text-neutral-600 mt-1">
              Step {currentStepIndex + 1} of {steps.length}
            </p>
          </div>
          
          <div className="flex items-center gap-3 text-sm text-neutral-600">
            <div className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              <span>{totalEstimatedTime - completedTime} min remaining</span>
            </div>
            <div className="text-neutral-400">•</div>
            <div className="font-medium text-primary-600">
              {Math.round((currentStepIndex / (steps.length - 1)) * 100)}% complete
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="relative mb-12">
          <div className="absolute inset-0 bg-neutral-200 rounded-full h-2"></div>
          <motion.div
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full h-2"
            initial={{ width: 0 }}
            animate={{ 
              width: `${(currentStepIndex / (steps.length - 1)) * 100}%` 
            }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>

        {/* Steps navigation */}
        <div className="flex items-start justify-between gap-4">
          {steps.map((step, index) => {
            const isCompleted = step.isCompleted;
            const isActive = step.isActive;
            const isClickable = onStepClick && (isCompleted || isActive || (index === currentStepIndex + 1 && steps[currentStepIndex]?.isCompleted));

            return (
              <motion.div
                key={step.id}
                className="flex-1 flex flex-col items-center text-center"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                {/* Step button */}
                <button
                  onClick={() => isClickable && onStepClick?.(index)}
                  disabled={!isClickable}
                  title={
                    isCompleted 
                      ? `${step.title} - Click to edit` 
                      : isActive
                        ? `${step.title} - Current step`
                        : isClickable
                          ? `${step.title} - Available`
                          : `${step.title} - Complete previous steps first`
                  }
                  className={`
                    group relative transition-all duration-200
                    ${isClickable ? 'cursor-pointer hover:scale-105' : 'cursor-default'}
                    ${isCompleted && !isActive ? 'hover:ring-2 hover:ring-primary-200 hover:ring-offset-4 rounded-full' : ''}
                  `}
                >
                  {/* Step circle */}
                  <div className={`
                    w-12 h-12 rounded-full border-2 flex items-center justify-center
                    transition-all duration-200 mb-4 relative
                    ${isCompleted 
                      ? 'bg-primary-500 border-primary-500 text-white shadow-lg' 
                      : isActive 
                        ? 'bg-white border-primary-500 text-primary-500 shadow-glow-primary ring-4 ring-primary-100' 
                        : isClickable
                          ? 'bg-white border-secondary-300 text-secondary-500'
                          : 'bg-neutral-100 border-neutral-300 text-neutral-400'
                    }
                    ${isClickable ? 'group-hover:shadow-strong' : ''}
                  `}>
                    {isCompleted ? (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.2 }}
                        className="flex items-center justify-center"
                      >
                        <Check className="w-6 h-6" />
                      </motion.div>
                    ) : (
                      <span className="text-lg font-bold">
                        {index + 1}
                      </span>
                    )}

                    {/* Clickable indicator for completed steps */}
                    {isCompleted && !isActive && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute -top-1 -right-1 w-5 h-5 bg-secondary-500 rounded-full flex items-center justify-center opacity-80 group-hover:opacity-100 transition-all group-hover:scale-110"
                      >
                        <span className="text-white text-xs font-bold">✎</span>
                      </motion.div>
                    )}
                  </div>
                </button>

                {/* Step content */}
                <div className="space-y-2 max-w-[140px]">
                  <h3 className={`
                    text-sm font-bold transition-colors duration-200 leading-tight
                    ${isActive ? 'text-primary-600' : isCompleted ? 'text-neutral-900' : isClickable ? 'text-secondary-600' : 'text-neutral-500'}
                    ${isClickable ? 'group-hover:text-primary-600' : ''}
                  `}>
                    {step.title}
                  </h3>
                  <p className={`
                    text-xs leading-relaxed transition-colors duration-200
                    ${isActive ? 'text-neutral-600' : 'text-neutral-400'}
                  `}>
                    {step.description}
                  </p>
                  {step.estimatedTime > 0 && (
                    <p className="text-xs text-neutral-400 flex items-center justify-center gap-1">
                      <Clock className="w-3 h-3" />
                      {step.estimatedTime} min
                    </p>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}; 